import React, { useEffect, useState } from 'react';
import { Bell, Loader2, ChevronRight, Download, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { isPushSupported, getPermission, hasActiveSubscription, subscribeToPush } from '../utils/pushClient';
import { isIOS, isStandalone, canPromptInstall, onInstallAvailable, promptInstall } from '../utils/pwaInstall';
import { NotificationHelpSheet } from './NotificationHelpSheet';

const SERIF = { fontFamily: "'Playfair Display', serif" };

// One-tap opt-in near the top of Profile. Hides itself once notifications are on; full controls stay in the Notifications section below.
export const PushOptInCard = () => {
  const [state, setState] = useState('loading'); // loading | off | on | blocked | unsupported | ios-install
  const [busy, setBusy] = useState(false);
  const [help, setHelp] = useState(null); // null | 'install' | 'blocked'
  const [installable, setInstallable] = useState(canPromptInstall());
  useEffect(() => onInstallAvailable(setInstallable), []);
  useEffect(() => {
    if (!isPushSupported()) { setState(isIOS() && !isStandalone() ? 'ios-install' : 'unsupported'); return; }
    if (getPermission() === 'denied') { setState('blocked'); return; }
    hasActiveSubscription().then((on) => setState(on ? 'on' : 'off'));
  }, []);
  if (state === 'loading' || state === 'on') return null;
  const enable = async () => {
    setBusy(true);
    try { await subscribeToPush(); setState('on'); toast.success('Notifications are on 🔔', { description: 'Gentle reminders, on your schedule. Adjust anytime below.' }); }
    catch (e) {
      if (e.message === 'Permission denied' || getPermission() === 'denied') { setState('blocked'); setHelp('blocked'); }
      else toast.error(e.message || 'Could not enable notifications');
    }
    finally { setBusy(false); }
  };
  const install = async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted') toast.success('Reflections installed — open it from your Home screen');
  };
  const scrollToSettings = () => document.querySelector('[data-testid="push-settings"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const copy = {
    'ios-install': ['Add Reflections to your Home Screen', 'Apple only allows notifications from apps on your Home Screen. Tap Share → “Add to Home Screen”, then open Reflections from there and tap Turn on.'],
    blocked: ['Notifications are blocked', 'Your phone or browser is blocking notifications for Reflections. Tap “Show me how” to fix it in about 30 seconds.'],
    unsupported: ['Notifications need a different browser', "This browser can't deliver notifications. Open Reflections in Safari (iPhone) or Chrome (Android) and try again."],
    off: ['Turn on notifications', 'A gentle nudge for your next journey day and news when Rowdy records something new. One tap — your phone will ask first.'],
  }[state];
  return (
    <section className="px-5 mb-6" data-testid="push-optin-card">
      <div className="relative overflow-hidden rounded-3xl bg-[#0f1e33] text-[#f9f4ea] p-4 shadow-lg ring-1 ring-white/10">
        <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-[#D6AD60]/15 blur-3xl" />
        <div className="relative flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#D6AD60]/20 flex items-center justify-center shrink-0"><Bell className="w-5 h-5 text-[#D6AD60]" /></div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#D6AD60]">Notifications</p>
            <h3 className="mt-0.5 text-base" style={SERIF} data-testid="push-optin-title">{copy[0]}</h3>
            <p className="mt-1 text-xs text-[#e3d2a8]/85 leading-relaxed">{copy[1]}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {state === 'off' && (
                <button type="button" onClick={enable} disabled={busy} className="h-11 px-5 rounded-full bg-[#D6AD60] text-[#0f1e33] text-xs font-semibold uppercase tracking-[0.2em] inline-flex items-center gap-2 hover:bg-[#c9a866] active:scale-[0.98] transition-all disabled:opacity-60" data-testid="push-optin-enable">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} Turn on notifications
                </button>
              )}
              {state === 'off' && installable && (
                <button type="button" onClick={install} className="h-11 px-4 rounded-full border border-[#D6AD60]/60 text-[#e3d2a8] text-xs font-semibold uppercase tracking-[0.18em] inline-flex items-center gap-2" data-testid="push-optin-install">
                  <Download className="w-4 h-4" /> Install app
                </button>
              )}
              {(state === 'ios-install' || state === 'blocked' || state === 'unsupported') && (
                <button type="button" onClick={() => setHelp(state === 'blocked' ? 'blocked' : 'install')} className="h-11 px-5 rounded-full bg-[#D6AD60] text-[#0f1e33] text-xs font-semibold uppercase tracking-[0.2em] inline-flex items-center gap-2 active:scale-[0.98] transition-all" data-testid="push-optin-help">
                  <HelpCircle className="w-4 h-4" /> Show me how
                </button>
              )}
            </div>
            <button type="button" onClick={scrollToSettings} className="mt-2 ml-1 text-[11px] text-[#e3d2a8]/80 inline-flex items-center gap-1" data-testid="push-optin-settings-link">Notification settings <ChevronRight className="w-3 h-3" /></button>
          </div>
        </div>
      </div>
      <NotificationHelpSheet open={!!help} mode={help || 'install'} onClose={() => setHelp(null)} />
    </section>
  );
};

export default PushOptInCard;
